import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '@/services/api';
import { patchApplicationsCache } from '@/lib/recruiterCache';
import { isApplicationStarred } from '@/lib/applicationStar';
import type { Application } from '@/types';

export function useApplicationStar(
  companyId: string | undefined,
  userId: string | undefined,
) {
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);

  const toggleStar = useCallback(async (app: Application) => {
    if (!companyId || !userId || busyId === app.id) return;
    const nextStarred = !isApplicationStarred(app, userId);
    setBusyId(app.id);

    const optimisticStarredBy = nextStarred
      ? Array.from(new Set([...(app.starredBy ?? []), userId]))
      : (app.starredBy ?? []).filter((uid) => uid !== userId);

    patchApplicationsCache(queryClient, companyId, [{
      ...app,
      starredBy: optimisticStarredBy.length > 0 ? optimisticStarredBy : undefined,
    }]);

    try {
      const updated = await api.applications.setStarred(
        companyId,
        app.positionId,
        app.id,
        nextStarred,
      );
      patchApplicationsCache(queryClient, companyId, [updated]);
      return updated;
    } catch {
      patchApplicationsCache(queryClient, companyId, [app]);
      throw new Error('star_failed');
    } finally {
      setBusyId(null);
    }
  }, [companyId, userId, busyId, queryClient]);

  return {
    toggleStar,
    isStarred: (app: Application) => isApplicationStarred(app, userId),
    isBusy: (appId: string) => busyId === appId,
  };
}
